import { globalLoggerToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import FetchAndXHRPatcher from "@lib/FetchAndXHRPatcher/FetchAndXHRPatcher";
import { SDK_MODULES } from "@modules/sdk/constants";
import { HooksManager } from "@modules/sdk/hooks/HooksManager";
import { SdkModule } from "@modules/sdk/SdkModule";
import { container, singleton } from "tsyringe";

import { ModuleInterface } from "@interfaces/ModuleInterface";

import { AjaxApiCartApiService } from "./api_services/AjaxApiCartApiService/AjaxApiCartApiService";
import { CartQuery } from "./queries/CartQuery";
import { AjaxApiRepository } from "./repositories/AjaxApiRepository";
import { CartRepositoryProvider } from "./services/CartRepositoryProvider";
import { CartService } from "./services/CartService";
import { CartStore } from "./stores/CartStore";
import CartListener from "./utils/CartListener/CartListener";
import CartRemovalListener from "./utils/CartListener/CartRemovalListener";
import CartRemovalListenerStrategy from "./utils/CartListener/CartRemovalListenerStrategy";
import FetchOrXHRPatcherListenerStrategy from "./utils/CartListener/FetchOrXHRPatcherListenerStrategy";
import SendFormListenerStrategy from "./utils/CartListener/SendFormListenerStrategy";
import CartManagerCounterUpdatersContext from "./utils/CartManager/CartManagerCounterUpdatersContext";
import CartManagerItemsUpdatersContext from "./utils/CartManager/CartManagerItemsUpdatersContext";

interface CartModuleExports {
    cartListener: CartListener;
    cartRemovalListener: CartRemovalListener;
    cartService: CartService;
    cartQuery: CartQuery;
}

@singleton()
export class CartModule implements ModuleInterface<CartModuleExports> {
    exports: CartModuleExports;

    public constructor() {
        const cartModuleLogger = container
            .resolve(globalLoggerToken)
            .newLogger("CartModule");

        const fetchAndXHRPatcher = new FetchAndXHRPatcher(
            container.resolve(globalLoggerToken)
        );
        fetchAndXHRPatcher.patch();

        const cartManagerCounterUpdatersContext =
            new CartManagerCounterUpdatersContext();

        const cartManagerItemsUpdatersContext =
            new CartManagerItemsUpdatersContext();

        const cartStore = new CartStore();

        const cartRepositoryProvider = new CartRepositoryProvider(
            new AjaxApiRepository(
                new AjaxApiCartApiService(new ApiClient("/"), cartModuleLogger)
            )
        );

        const cartService = new CartService(
            cartRepositoryProvider,
            cartModuleLogger,
            cartManagerCounterUpdatersContext,
            cartManagerItemsUpdatersContext,
            cartStore,
            container.resolve(HooksManager)
        );

        const cartQuery = new CartQuery(cartStore);

        // Prepare SDK module for dynamic import
        void (async () => {
            const { sdkDynamicModulesManager, sdk } =
                container.resolve(SdkModule).exports;

            const CartSdk = await sdkDynamicModulesManager.import(
                SDK_MODULES.CART
            );
            const cartSdk = new CartSdk(cartRepositoryProvider);
            sdk.registerModule(SDK_MODULES.CART, cartSdk);
        })();

        // (utils) cartListener
        const cartListener = new CartListener(cartModuleLogger);
        cartListener.register(new SendFormListenerStrategy(cartModuleLogger));
        cartListener.register(
            new FetchOrXHRPatcherListenerStrategy(
                cartModuleLogger,
                fetchAndXHRPatcher
            )
        );

        const cartRemovalListener = new CartRemovalListener(cartModuleLogger);
        cartRemovalListener.register(
            new CartRemovalListenerStrategy(
                cartModuleLogger,
                fetchAndXHRPatcher
            )
        );

        this.exports = {
            cartListener,
            cartRemovalListener,
            cartQuery,
            cartService,
        };
    }

    registerProviders() {
        return {};
    }
}
